import { toothedBandMaxHalfHeight, toothedBandMaxRadius } from './toothedBand.js';
const DEFAULT_SPHERE_RADIUS = 0.6;
const DEFAULT_SPHERE_SEGMENTS = 20;
const DEFAULT_ANNULAR_SEGMENTS = 96;
const DEFAULT_ANNULAR_INNER_COLOR = '#8f8f8f';
const DEFAULT_ANNULAR_OUTER_COLOR = '#4f4f4f';
const DEFAULT_ANNULAR_GRADIENT_CUTOFF_CENTER = 0.5;
const DEFAULT_ANNULAR_GRADIENT_CUTOFF_WIDTH = 0.12;
const DEFAULT_TUBE_RADIUS = 0.1;
const DEFAULT_TORUS_RADIAL_SEGMENTS = 10;
const DEFAULT_TORUS_TUBULAR_SEGMENTS = 96;
const DEFAULT_SECTOR_ANGULAR_GAP_FRACTION = 0.16;
const DEFAULT_SECTOR_ARC_SEGMENTS = 24;
const DEFAULT_CYLINDER_WALL_ARC_SEGMENTS = 48;
/** Depth bias that keeps a panel outline in front of its own face. */
export const PANEL_OUTLINE_Z_EPSILON = 0.04;
export function geometrySpecKey(spec) {
    switch (spec.geometryKind) {
        case 'revolved-profile':
            return `revolved-profile:${spec.segments}:${JSON.stringify(spec.profile)}`;
        case 'toothed-band':
            return `toothed-band:${spec.innerRadius}:${spec.outerRadius}:${spec.halfHeight}:${spec.toothAxis}:${spec.toothCount}:${spec.toothDepth}:${spec.segments}:${spec.edgeProfile ?? 'serrated'}`;
        case 'sphere':
            return `sphere:${spec.radius}:${spec.widthSegments}:${spec.heightSegments}`;
        case 'panel':
            return `panel:${spec.width}:${spec.height}:${spec.curvatureRadius ?? 'flat'}`;
        case 'panel-outline':
            return `panel-outline:${spec.width}:${spec.height}:${spec.z}:${spec.curvatureRadius ?? 'flat'}`;
        case 'sector-slice-outline':
            return `sector-slice-outline:${spec.innerRadius}:${spec.outerRadius}:${spec.startAngle}:${spec.endAngle}:${spec.arcSegments}:${spec.outerThickness ?? 0}:${spec.thicknessProfile ?? 'tapered'}`;
        case 'torus':
            return `torus:${spec.ringRadius}:${spec.tubeRadius}:${spec.radialSegments}:${spec.tubularSegments}`;
        case 'annular-band':
            return [
                'annular-band',
                spec.innerRadius,
                spec.outerRadius,
                spec.segments,
                spec.innerColor,
                spec.outerColor,
                spec.gradientCutoffCenter,
                spec.gradientCutoffWidth,
            ].join(':');
        case 'sector-slice':
            return [
                'sector-slice',
                spec.itemCount,
                spec.innerRadius,
                spec.outerRadius,
                spec.angularGapFraction,
                spec.arcSegments,
                spec.outerThickness ?? 0,
                spec.thicknessProfile ?? 'tapered',
            ].join(':');
        case 'cylinder-wall':
            return `cylinder-wall:${spec.radius}:${spec.arcSegments}:${spec.half ? 'half' : 'full'}`;
        case 'connector-tube':
            return `connector-tube:${spec.radialSegments}`;
        case 'extruded-profile':
            return `extruded-profile:${spec.surface}:${spec.innerWidth}:${spec.lipThickness}:${spec.lipHeight}:${spec.floorDepth}:${spec.outline ? 'outline' : 'filled'}`;
    }
}
function toSafeSegmentCount(value, fallback, minimum) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return fallback;
    return Math.max(minimum, Math.round(value));
}
function defaultSphereSpec() {
    return {
        geometryKind: 'sphere',
        radius: DEFAULT_SPHERE_RADIUS,
        widthSegments: DEFAULT_SPHERE_SEGMENTS,
        heightSegments: DEFAULT_SPHERE_SEGMENTS,
    };
}
function annularBandSpec(shape) {
    const innerRadius = Math.max(0.0001, shape.innerRadius);
    const outerRadius = Math.max(innerRadius + 0.0001, shape.outerRadius);
    return {
        geometryKind: 'annular-band',
        innerRadius,
        outerRadius,
        segments: toSafeSegmentCount(shape.segments, DEFAULT_ANNULAR_SEGMENTS, 16),
        innerColor: shape.innerColor ?? DEFAULT_ANNULAR_INNER_COLOR,
        outerColor: shape.outerColor ?? DEFAULT_ANNULAR_OUTER_COLOR,
        gradientCutoffCenter: shape.gradientCutoffCenter ?? DEFAULT_ANNULAR_GRADIENT_CUTOFF_CENTER,
        gradientCutoffWidth: shape.gradientCutoffWidth ?? DEFAULT_ANNULAR_GRADIENT_CUTOFF_WIDTH,
    };
}
function concentricRingTorusSpec(args) {
    const { layout, itemIndex } = args;
    const fallbackTubeRadius = layout.ringTube.tubeRadius > 0 ? layout.ringTube.tubeRadius : DEFAULT_TUBE_RADIUS;
    const ringRadius = layout.ringRadii?.[itemIndex] ?? (layout.startRadius + itemIndex * layout.ringGap);
    const crossSection = layout.ringTube.crossSectionsByRing?.[itemIndex] ?? 'tube';
    if (crossSection !== 'tube') {
        if (crossSection !== 'horizontal-band' && crossSection !== 'vertical-band') {
            throw new Error(`Unsupported concentric ring cross-section: ${crossSection}`);
        }
        const ratio = layout.ringTube.bandThicknessRatio ?? 0.15;
        const tube = layout.ringTube.tubeRadii?.[itemIndex] ?? fallbackTubeRadius;
        if (!Number.isFinite(ratio) || ratio <= 0 || ratio > 1
            || !Number.isFinite(tube) || tube <= 0 || !Number.isFinite(ringRadius) || ringRadius <= tube) {
            throw new Error('Concentric ring bands require positive extents, an open center, and a thickness ratio in (0, 1].');
        }
        const broad = layout.ringTube.bandWidth === undefined ? tube : layout.ringTube.bandWidth / 2;
        const thin = layout.ringTube.bandThickness === undefined ? tube * ratio : layout.ringTube.bandThickness / 2;
        if (!Number.isFinite(broad) || !Number.isFinite(thin) || thin <= 0 || broad < thin || ringRadius <= broad) {
            throw new Error('Concentric ring band dimensions require finite positive thickness <= width and an open center.');
        }
        const radial = crossSection === 'vertical-band' ? thin : broad;
        const height = crossSection === 'horizontal-band' ? thin : broad;
        const segments = toSafeSegmentCount(layout.ringTube.tubularSegments, DEFAULT_TORUS_TUBULAR_SEGMENTS, 16);
        const teeth = layout.ringTube.bandTeeth;
        if (teeth !== undefined) {
            const edge = teeth.profilesByRing?.[itemIndex];
            const count = edge?.count ?? teeth.count;
            if (edge && edge.profile !== 'serrated' && edge.profile !== 'scalloped') {
                throw new Error('Unsupported concentric ring band teeth profile.');
            }
            const toothAxis = crossSection === 'horizontal-band' ? 'radial' : 'axial';
            if (!Number.isInteger(count) || count < 3 || !Number.isFinite(teeth.depth) || teeth.depth <= 0
                || (toothAxis === 'radial' && ringRadius - radial - teeth.depth <= 0)) {
                throw new Error('Concentric ring band teeth require an integer count >= 3 and a positive depth that keeps the center open.');
            }
            return {
                geometryKind: 'toothed-band',
                innerRadius: ringRadius - radial,
                outerRadius: ringRadius + radial,
                halfHeight: height,
                toothAxis,
                toothCount: count,
                toothDepth: teeth.depth,
                ...(edge ? { edgeProfile: edge.profile } : {}),
                segments,
            };
        }
        return {
            geometryKind: 'revolved-profile',
            profile: [
                [ringRadius - radial, -height], [ringRadius + radial, -height],
                [ringRadius + radial, height], [ringRadius - radial, height],
            ],
            segments,
        };
    }
    return {
        geometryKind: 'torus',
        ringRadius: Math.max(0.0001, ringRadius),
        tubeRadius: Math.max(0.0001, layout.ringTube.tubeRadii?.[itemIndex] ?? fallbackTubeRadius),
        radialSegments: toSafeSegmentCount(layout.ringTube.tubeRadialSegmentsByRing?.[itemIndex], toSafeSegmentCount(layout.ringTube.radialSegments, DEFAULT_TORUS_RADIAL_SEGMENTS, 3), 3),
        tubularSegments: toSafeSegmentCount(layout.ringTube.tubularSegments, DEFAULT_TORUS_TUBULAR_SEGMENTS, 8),
    };
}
/**
 * Geometry is shape-driven for item-controlled layouts and layout-driven where the
 * layout owns the item footprint (sector-ring slices, concentric-ring tubes).
 */
export function resolveItemGeometrySpec(args) {
    const { layout, shape, itemIndex, itemCount } = args;
    if (layout.layoutKind === 'sector-ring') {
        return {
            geometryKind: 'sector-slice',
            itemCount,
            innerRadius: layout.innerRadius ?? 0,
            outerRadius: layout.outerRadius,
            angularGapFraction: layout.angularGapFraction ?? DEFAULT_SECTOR_ANGULAR_GAP_FRACTION,
            arcSegments: layout.arcSegments ?? DEFAULT_SECTOR_ARC_SEGMENTS,
            ...(layout.outerThickness === undefined ? {} : { outerThickness: layout.outerThickness }),
            ...(layout.thicknessProfile === undefined ? {} : { thicknessProfile: layout.thicknessProfile }),
        };
    }
    if (shape?.shapeKind === 'annular-band') {
        return annularBandSpec(shape);
    }
    if (shape?.shapeKind === 'revolved-profile') {
        validateRevolvedProfile(shape.profile);
        if (shape.segments !== undefined
            && (!Number.isInteger(shape.segments) || shape.segments < 16)) {
            throw new Error('Revolved profile segments must be an integer of at least 16.');
        }
        return { geometryKind: 'revolved-profile', profile: shape.profile, segments: shape.segments ?? 192 };
    }
    if (shape?.shapeKind === 'cylinder-wall') {
        return resolveCylinderWallGeometrySpecs(shape)[0] ?? defaultSphereSpec();
    }
    if (shape?.shapeKind === 'extruded-profile') {
        return resolveExtrudedProfileGeometrySpecs(shape)[0];
    }
    if (layout.layoutKind === 'concentric-ring') {
        return concentricRingTorusSpec({ layout, itemIndex });
    }
    if (shape?.shapeKind === 'panel') {
        if (shape.curvatureRadius !== undefined && (!Number.isFinite(shape.curvatureRadius)
            || shape.curvatureRadius <= 0 || !Number.isFinite(shape.width) || shape.width <= 0
            || shape.width / shape.curvatureRadius > Math.PI)) {
            throw new Error('Curved panels require a positive finite radius and arc width no greater than half a circle.');
        }
        return {
            geometryKind: 'panel',
            width: shape.width,
            height: shape.height,
            ...(shape.curvatureRadius === undefined ? {} : { curvatureRadius: shape.curvatureRadius }),
        };
    }
    if (shape?.shapeKind === 'ring-guide') {
        return {
            geometryKind: 'torus',
            ringRadius: shape.radius,
            tubeRadius: Math.min(DEFAULT_TUBE_RADIUS, shape.radius / 8),
            radialSegments: DEFAULT_TORUS_RADIAL_SEGMENTS,
            tubularSegments: toSafeSegmentCount(shape.segments, DEFAULT_TORUS_TUBULAR_SEGMENTS, 16),
        };
    }
    if (shape?.shapeKind === 'sphere') {
        const radius = typeof shape.radius === 'number' && shape.radius > 0 ? shape.radius : DEFAULT_SPHERE_RADIUS;
        return {
            geometryKind: 'sphere',
            radius,
            widthSegments: toSafeSegmentCount(shape.widthSegments, DEFAULT_SPHERE_SEGMENTS, 3),
            heightSegments: toSafeSegmentCount(shape.heightSegments, DEFAULT_SPHERE_SEGMENTS, 2),
        };
    }
    return defaultSphereSpec();
}
export function resolveItemOutlineGeometrySpec(shape) {
    if (shape === undefined
        || (shape.shapeKind !== 'panel' && shape.shapeKind !== 'sector-slice')
        || (shape.appearanceMode !== 'outlined' && typeof shape.border?.color !== 'string'))
        return null;
    const offset = typeof shape.border?.offset === 'number' ? Math.max(0, shape.border.offset) : 0;
    if (shape.shapeKind === 'sector-slice') {
        const midRadius = (shape.innerRadius + shape.outerRadius) / 2;
        const fullCircle = Math.abs(Math.abs(shape.endAngle - shape.startAngle) - Math.PI * 2) <= 1e-6;
        const angularOffset = fullCircle || midRadius <= 0 ? 0 : offset / midRadius;
        return {
            geometryKind: 'sector-slice-outline',
            innerRadius: Math.max(0, shape.innerRadius - offset),
            outerRadius: shape.outerRadius + offset,
            startAngle: shape.startAngle - angularOffset,
            endAngle: shape.endAngle + angularOffset,
            arcSegments: toSafeSegmentCount(shape.arcSegments, DEFAULT_SECTOR_ARC_SEGMENTS, 3),
            ...(shape.outerThickness === undefined ? {} : { outerThickness: shape.outerThickness }),
            ...(shape.thicknessProfile === undefined ? {} : { thicknessProfile: shape.thicknessProfile }),
        };
    }
    if (shape.shapeKind !== 'panel')
        return null;
    return {
        geometryKind: 'panel-outline',
        width: shape.width + offset * 2,
        height: shape.height + offset * 2,
        z: PANEL_OUTLINE_Z_EPSILON,
        ...(shape.curvatureRadius === undefined ? {} : { curvatureRadius: shape.curvatureRadius }),
    };
}
/** Fallback spacing used when a layout does not declare an explicit item spacing. */
export function geometrySpecSpacingDiameter(spec) {
    switch (spec.geometryKind) {
        case 'revolved-profile':
            return Math.max(...spec.profile.map(([radius, height]) => Math.hypot(radius, height))) * 2;
        case 'toothed-band':
            return Math.hypot(toothedBandMaxRadius(spec), toothedBandMaxHalfHeight(spec)) * 2;
        case 'sphere':
            return spec.radius * 2;
        case 'panel':
        case 'panel-outline':
            return Math.hypot(spec.width, spec.height);
        case 'sector-slice-outline':
            return spec.outerRadius * 2;
        case 'torus':
            return (spec.ringRadius + spec.tubeRadius) * 2;
        case 'annular-band':
            return spec.outerRadius * 2;
        case 'sector-slice':
            return spec.outerRadius * 2;
        case 'cylinder-wall':
            return spec.radius * 2;
        case 'connector-tube':
            return 1;
        case 'extruded-profile':
            return spec.innerWidth + spec.lipThickness * 2;
    }
}
function validateRevolvedProfile(profile) {
    if (profile.length < 3 || profile.some(([radius, height]) => !Number.isFinite(radius) || radius <= 0 || !Number.isFinite(height))) {
        throw new Error('Revolved profile requires at least three finite vertices with positive radii.');
    }
    let area = 0;
    for (let index = 0; index < profile.length; index += 1) {
        const a = profile[index];
        const b = profile[(index + 1) % profile.length];
        if (a[0] === b[0] && a[1] === b[1]) {
            throw new Error('Revolved profile vertices must not repeat consecutively.');
        }
        area += a[0] * b[1] - b[0] * a[1];
        for (const point of profile) {
            const cross = (b[0] - a[0]) * (point[1] - a[1]) - (b[1] - a[1]) * (point[0] - a[0]);
            if (cross < -1e-9) {
                throw new Error('Revolved profile must be convex and counterclockwise.');
            }
        }
    }
    if (area <= 0)
        throw new Error('Revolved profile must enclose a positive area.');
}
/** One unit-height surface per non-zero radius, outer first. */
export function resolveCylinderWallGeometrySpecs(shape) {
    const arcSegments = toSafeSegmentCount(shape.arcSegments, DEFAULT_CYLINDER_WALL_ARC_SEGMENTS, 3);
    return [shape.outerRadius, shape.innerRadius]
        .filter((radius) => radius > 0)
        .map((radius) => ({ geometryKind: 'cylinder-wall', radius, arcSegments, half: shape.cutaway }));
}
export function resolveExtrudedProfileGeometrySpecs(shape) {
    const dimensions = {
        geometryKind: 'extruded-profile',
        innerWidth: Math.max(0.0001, shape.innerWidth),
        lipThickness: Math.max(0.0001, shape.lipThickness),
        lipHeight: Math.max(0.0001, shape.lipHeight),
        floorDepth: shape.floorDepth === 0 ? 0 : Math.max(0.0001, shape.floorDepth),
    };
    return (shape.profileKind === 'bar'
        ? ['bar']
        : shape.floorDepth === 0 ? ['left-lip', 'right-lip']
            : ['floor', 'left-lip', 'right-lip']).map((surface) => ({ ...dimensions, surface }));
}
